/**
 * TanStack Query hooks for notifications.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@/services/notification.service';
import type { NotificationsResult } from '@/services/notification.service';

const NOTIFICATIONS_KEY = ['notifications'] as const;

/** Fetch all notifications */
export function useNotifications() {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => notificationService.getNotifications(),
    staleTime: 5 * 60_000, // 5 minutes
    refetchInterval: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/** Mark a single notification as read */
export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => notificationService.markAsRead(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      const prev = queryClient.getQueryData<NotificationsResult>(NOTIFICATIONS_KEY);
      if (prev) {
        queryClient.setQueryData<NotificationsResult>(NOTIFICATIONS_KEY, {
          ...prev,
          unreadCount: Math.max(0, prev.unreadCount - 1),
          notifications: prev.notifications.map((n) =>
            n.id === notificationId ? { ...n, read: true } : n,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _id, context) => {
      if (context?.prev) queryClient.setQueryData(NOTIFICATIONS_KEY, context.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

/** Mark all notifications as read */
export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      const prev = queryClient.getQueryData<NotificationsResult>(NOTIFICATIONS_KEY);
      if (prev) {
        queryClient.setQueryData<NotificationsResult>(NOTIFICATIONS_KEY, {
          ...prev,
          unreadCount: 0,
          notifications: prev.notifications.map((n) => ({ ...n, read: true })),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) queryClient.setQueryData(NOTIFICATIONS_KEY, context.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

/** Dismiss a notification */
export function useDismissNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => notificationService.dismiss(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      const prev = queryClient.getQueryData<NotificationsResult>(NOTIFICATIONS_KEY);
      if (prev) {
        const removed = prev.notifications.find((n) => n.id === notificationId);
        queryClient.setQueryData<NotificationsResult>(NOTIFICATIONS_KEY, {
          ...prev,
          unreadCount: removed && !removed.read ? prev.unreadCount - 1 : prev.unreadCount,
          notifications: prev.notifications.filter((n) => n.id !== notificationId),
        });
      }
      return { prev };
    },
    onError: (_err, _id, context) => {
      if (context?.prev) queryClient.setQueryData(NOTIFICATIONS_KEY, context.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}
